import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BellRing, Receipt, Clock, LayoutGrid, Plus, RotateCcw, Star } from "lucide-react";
import { RestaurantBrand } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { OrderStatusTimeline } from "@/components/OrderStatusTimeline";
import { LoyaltyCard } from "@/components/LoyaltyCard";
import { isApiMenuSource } from "@/config/menuSource";
import { NoActiveOrder } from "@/components/EmptyStates";
import { useLang } from "@/context/LanguageContext";
import { useOrder } from "@/context/OrderContext";
import { useUI } from "@/context/UIContext";
import { useCart } from "@/context/CartContext";
import { restaurant } from "@/data/restaurant";
import { localize, formatTRY } from "@/lib/format";
import { buildReorder } from "@/lib/reorder";
import { toast } from "sonner";

export default function OrderPage() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const { order, activeOrder, actions } = useOrder();
  const { openServices, openStaffRating } = useUI();
  const { addItems } = useCart();

  // Rebuild with current menu/combo config + prices; report anything skipped.
  const reorder = () => {
    if (!order) return;
    const { lines, skipped } = buildReorder(order);
    if (lines.length) {
      addItems(lines);
      toast.success(t("reorderDone"));
    }
    if (skipped.length) {
      const names = skipped.map((s) => (s.kind === "combo" ? `${localize(s.name, lang)} ${t("reorderComboTag")}` : localize(s.name, lang)));
      toast.warning(t("reorderSkipped"), { description: names.join(", "), duration: 8000 });
    }
  };

  return (
    <div data-testid="order-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 glass pt-safe">
        <div className="flex items-center justify-between px-4 py-3">
          <RestaurantBrand name={restaurant.name} branch={restaurant.branch.name} logoImage={restaurant.logoImage} />
          <div className="flex items-center gap-2">
            <button onClick={openServices} className="grid h-10 w-10 place-items-center rounded-full border border-border bg-card shadow-soft active:scale-95" aria-label={t("services")} data-testid="services-btn">
              <LayoutGrid className="h-5 w-5 text-primary" />
            </button>
            <LanguageSelector />
            <ThemeToggle />
          </div>
        </div>
      </header>

      {!order ? (
        <NoActiveOrder onBrowse={() => navigate("/menu")} />
      ) : (
        <div className="space-y-5 px-4 py-5">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft"
          >
            <div className="flex items-center justify-between brand-gradient px-5 py-4 text-white">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-white/80">{t("orderNumber")}</p>
                <p className="font-heading text-2xl font-extrabold">#{order.number}</p>
              </div>
              {order.tableNumber != null && (
                <div className="text-end">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/80">{t("table")}</p>
                  <p className="font-heading text-2xl font-extrabold">{order.tableNumber}</p>
                </div>
              )}
            </div>
            {order.estReadyMin != null && (
              <div className="flex items-center gap-2 border-b border-border px-5 py-3 text-sm">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">{t("estReady")}</span>
                <span className="font-semibold">~{order.estReadyMin} {t("min")}</span>
              </div>
            )}
            <div className="p-5">
              <h3 className="mb-4 font-heading text-base font-bold">{t("orderStatus")}</h3>
              <OrderStatusTimeline status={order.status} />
            </div>
          </motion.div>

          {/* Items */}
          <section className="rounded-3xl border border-border bg-card p-4 shadow-soft">
            <h3 className="mb-3 font-heading text-base font-bold">
              {order.items.length} {t("items")}
            </h3>
            <div className="space-y-3">
              {order.items.map((it) => (
                <div key={it.lineId} className="flex items-center gap-3" data-testid={`order-item-${it.lineId}`}>
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted">
                    <img src={it.image} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm font-semibold">{localize(it.name, lang)}</p>
                    <p className="text-xs text-muted-foreground">x{it.quantity}</p>
                  </div>
                  <span className="font-heading text-sm font-bold">{formatTRY(it.lineTotal)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
              <span className="font-heading text-base font-bold">{t("total")}</span>
              <span className="font-heading text-xl font-extrabold text-primary" data-testid="order-total">
                {formatTRY(order.total)}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <button
                onClick={() => navigate("/menu")}
                className="flex items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/5 py-3 text-sm font-bold text-primary active:scale-[0.99]"
                data-testid={activeOrder ? "add-to-order-btn" : "new-order-btn"}
              >
                <Plus className="h-4 w-4" /> {activeOrder ? t("addToOrder") : t("newOrder")}
              </button>
              <button
                onClick={reorder}
                className="flex items-center justify-center gap-2 rounded-full border border-border bg-card py-3 text-sm font-bold active:scale-[0.99]"
                data-testid="reorder-btn"
              >
                <RotateCcw className="h-4 w-4" /> {t("reorder")}
              </button>
            </div>
          </section>

          {!isApiMenuSource() && (
            <button
              onClick={openStaffRating}
              className="flex w-full items-center justify-center gap-2 rounded-3xl border border-border bg-card py-4 text-sm font-bold shadow-soft active:scale-[0.99]"
              data-testid="rate-waiter-btn"
            >
              <Star className="h-4 w-4 text-amber-400" /> {t("rateWaiter")}
            </button>
          )}

          <LoyaltyCard />

          {/* Recent actions */}
          {actions.length > 0 && (
            <section className="rounded-3xl border border-border bg-card p-4 shadow-soft">
              <h3 className="mb-3 font-heading text-base font-bold">{t("recentActions")}</h3>
              <div className="space-y-2.5">
                {actions.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center gap-3" data-testid={`action-${a.id}`}>
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
                      {a.type === "waiter" ? <BellRing className="h-4 w-4" /> : <Receipt className="h-4 w-4" />}
                    </span>
                    <span className="flex-1 text-sm font-medium">
                      {a.type === "waiter" ? t(a.reasonKey || "callWaiter") : t("requestBill")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(a.at).toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
